import React from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../stores/language-store';
import { SidebarProvider, useSidebar } from '../components/sidebar/sidebar-provider';
import { AppSidebar } from '../components/sidebar/app-sidebar';
import Header from '../components/header';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayoutContent: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { isRTL } = useLanguage();
  const location = useLocation();
  const { isOpen, isMini, isMobile } = useSidebar();

  // Check if current page is a fullpage layout (only conversations page needs overflow-hidden)
  const isFullPageLayout = location.pathname === '/chat/conversations';

  // Calculate sidebar width for margin (desktop only)
  const getSidebarWidth = () => {
    // On mobile, sidebar is a drawer (overlay), so no margin needed
    if (isMobile) return '0px';
    
    // On desktop, calculate based on state
    if (!isOpen) return '0px';
    return isMini ? '70px' : '280px';
  };

  return (
    <div className={`relative h-screen bg-gray-50 dark:bg-gray-900 ${isRTL ? 'rtl' : 'ltr'}`}>
      <AppSidebar />

      <div
        className="flex flex-col overflow-hidden transition-all duration-300"
        style={{
          marginLeft: isRTL ? '0' : getSidebarWidth(),
          marginRight: isRTL ? getSidebarWidth() : '0',
          width: isMobile ? '100%' : `calc(100% - ${getSidebarWidth()})`,
          height: '100vh'
        }}
      >
        <Header />
        
      <main className={`flex-1 bg-gray-50 dark:bg-gray-900 dark:text-gray-100 ${
        isFullPageLayout 
          ? 'overflow-hidden p-0 h-[calc(100vh-64px)]' 
          : 'overflow-y-auto p-4 sm:p-6 lg:p-10'
      }`}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={isFullPageLayout ? 'h-full' : ''}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  return (
    <SidebarProvider>
      <DashboardLayoutContent>{children}</DashboardLayoutContent>
    </SidebarProvider>
  );
};

export default DashboardLayout;